import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';

import { apiUrlGenerator } from '../use-api';
import { showResult } from '../utils';

import type {
  INormalReadingsReportResponse,
  IPartialPaginationMeta,
} from 'infrastructure/interfaces';

const url = 'reports/reports/normal-readings';
export const NORMAL_READINGS_REPORT_PER_PAGE = 40;

export type TProps = {
  startDate?: number;
  endDate?: number;
  interactionStatus?: boolean;
  clinicGuid?: string;
  doNotCall?: boolean;
  page: number;
  doctorLoginGuid?: string;
  agencyDoctorLoginGuid?: string;
  lastInteractionSort?: 'asc' | 'desc';
};

export type TNormalReadingsReportFilter = TProps & IPartialPaginationMeta;

const getNormalReadingsReportFn = async (
  filter: TNormalReadingsReportFilter,
) => {
  const { data, error } =
    await dataService.getList<INormalReadingsReportResponse>(
      apiUrlGenerator(url, filter),
    );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const getNormalReadingsReportKey = (
  filter: TNormalReadingsReportFilter,
) => ['getNormalReadingsReport', filter];

export const useGetNormalReadingsReport = (
  props: TNormalReadingsReportFilter,
) => {
  const filter: TNormalReadingsReportFilter = {
    ...props,
    items: NORMAL_READINGS_REPORT_PER_PAGE,
  };

  const { data, isLoading, isFetching, error } = useQuery<
    INormalReadingsReportResponse | undefined,
    string | undefined
  >({
    queryKey: getNormalReadingsReportKey(filter),
    queryFn: async () => getNormalReadingsReportFn(filter),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return { data, isLoading, isFetching };
};
